import React, { useEffect, useState } from "react";
import "./App.css";
import CrmDashboard from "./CrmDashboard";

const API = "/api";

function App() {
  const [page, setPage] = useState(
    window.location.hash.replace("#", "") || "home"
  );
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("potech_user") || "null"); }
    catch { return null; }
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [leads, setLeads] = useState([]);
  const [leadsMessage, setLeadsMessage] = useState("");

  useEffect(() => {
    const changePage = () => {
      setPage(window.location.hash.replace("#", "") || "home");
    };
    window.addEventListener("hashchange", changePage);
    return () => window.removeEventListener("hashchange", changePage);
  }, []);

  useEffect(() => {
    if ((page === "dashboard" || page === "leads") && user) loadLeads();
  }, [page, user]);

  function navigate(next) {
    window.location.hash = next === "home" ? "" : next;
    setPage(next);
    setError("");
    setNotice("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function login(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.token) {
        throw new Error(data.message || data.error || "Login failed. Please check your email and password.");
      }
      localStorage.setItem("potech_token", data.token);
      localStorage.setItem("potech_user", JSON.stringify(data.user || {}));
      localStorage.setItem("potech_org", JSON.stringify(data.organization || {}));
      setUser(data.user || {});
      navigate("dashboard");
    } catch (err) {
      setError(err.message || "Server connection failed. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function loadLeads() {
    const token = localStorage.getItem("potech_token");
    if (!token) return;
    try {
      const res = await fetch(`${API}/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        setLeadsMessage(
          res.status === 404
            ? "Lead list API route is not available yet. Your dashboard and public lead form remain separate."
            : `Lead list could not be loaded (HTTP ${res.status}).`
        );
        return;
      }
      const data = await res.json();
      const rows = Array.isArray(data) ? data :
        Array.isArray(data.content) ? data.content :
        Array.isArray(data.leads) ? data.leads : [];
      setLeads(rows);
      setLeadsMessage(rows.length ? "" : "No leads to display yet.");
    } catch {
      setLeadsMessage("Unable to load leads right now.");
    }
  }

  async function addLead(e) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const payload = {
      fullName: String(fd.get("fullName") || "").trim(),
      phone: String(fd.get("phone") || "").trim(),
      email: String(fd.get("email") || "").trim() || null,
      companyName: String(fd.get("service") || "").trim() || null,
      source: String(fd.get("source") || "MANUAL"),
      notes: String(fd.get("notes") || "").trim() || null,
      nextFollowUp: String(fd.get("nextFollowUp") || "") || null
    };

    setBusy(true);
    setError("");
    setNotice("");

    try {
      const token = localStorage.getItem("potech_token");
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || data.error || `Lead save failed (HTTP ${res.status})`);
      }

      form.reset();
      setNotice("Lead successfully added.");
      await loadLeads();
    } catch (err) {
      setError(err.message || "Unable to save lead. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  function logout() {
    localStorage.removeItem("potech_token");
    localStorage.removeItem("potech_user");
    localStorage.removeItem("potech_org");
    setUser(null);
    setLeads([]);
    navigate("home");
  }

  if ((page === "dashboard" || page === "leads") && user && localStorage.getItem("potech_token")) {
    return <CrmDashboard onLogout={logout} />;
  }

  if (page === "login") {
    return (
      <div className="login-layout">
        <div className="login-left">
          <button className="brand brand-light" onClick={() => navigate("home")}><span className="brand-mark">P</span><span>POTECH <b>CRM</b></span></button>
          <div className="login-promo">
            <span className="eyebrow light-eyebrow">ONE WORKSPACE. MORE GROWTH.</span>
            <h1>Every lead matters.<br/><span>Every follow-up counts.</span></h1>
            <p>Bring customer enquiries, follow-ups and your business services together with Potech CRM.</p>
            <div className="login-benefit"><span>✓</span> Keep customer enquiries organized</div>
            <div className="login-benefit"><span>✓</span> Stay on top of follow-ups</div>
            <div className="login-benefit"><span>✓</span> Grow with a clearer sales pipeline</div>
          </div>
          <div className="login-copyright">© {new Date().getFullYear()} Potech Technologies Pvt Ltd.</div>
        </div>
        <div className="login-right">
          <button className="back-link" onClick={() => navigate("home")}>← Back to website</button>
          <form className="login-card" onSubmit={login}>
            <div className="login-icon">↗</div>
            <span className="eyebrow">WELCOME BACK</span>
            <h2>Sign in to your account</h2>
            <p className="muted">Enter your account details to continue.</p>
            {error && <div className="alert-error">{error}</div>}
            <label htmlFor="email">Email address</label>
            <input id="email" type="email" placeholder="you@company.com" autoComplete="username" value={email} onChange={e => setEmail(e.target.value)} required/>
            <label htmlFor="password">Password</label>
            <input id="password" type="password" placeholder="Enter your password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} required/>
            <button className="primary-btn login-submit" disabled={busy}>{busy ? "Signing in..." : "Sign in to CRM"} <span>→</span></button>
            <div className="secure-note">▣ &nbsp; Your account is protected by secure authentication.</div>
            <div className="login-help">Need an account? <button type="button" onClick={() => navigate("home")}>Contact our team</button></div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="marketing">
      <header className="site-header">
        <div className="nav-inner">
          <button className="brand" onClick={() => navigate("home")}><span className="brand-mark">P</span><span>POTECH <b>CRM</b></span></button>
          <nav className="main-nav"><a href="#features">Features</a><a href="#services">Services</a><a href="#about">Why Potech?</a></nav>
          <div className="nav-actions"><button className="nav-login" onClick={() => navigate("login")}>Log in</button><button className="primary-btn nav-cta" onClick={() => { document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }); }}>Get started <span>→</span></button></div>
        </div>
      </header>
      <main>
        <section className="hero">
          <div className="hero-glow"></div>
          <div className="hero-content">
            <div className="hero-pill"><span className="live-dot"></span> CUSTOMER RELATIONSHIPS, SIMPLIFIED</div>
            <h1>Turn every enquiry<br/>into a <span>new opportunity.</span></h1>
            <p className="hero-copy">Meet Potech CRM — the smarter way to capture leads, manage customer conversations and stay ahead of every follow-up.</p>
            <div className="hero-actions"><button className="primary-btn hero-btn" onClick={() => navigate("login")}>Access your CRM <span>→</span></button><a className="secondary-btn" href="#features">Explore features <span>↓</span></a></div>
            <div className="hero-trust"><span>✓ Lead management</span><span>✓ Follow-up tracking</span><span>✓ Service enquiries</span></div>
          </div>
          <div className="hero-visual">
            <div className="visual-window">
              <div className="window-bar"><div className="window-dots"><i></i><i></i><i></i></div><span>potech CRM / overview</span><span className="window-status">● Live workspace</span></div>
              <div className="mock-content"><div className="mock-title"><div><small>YOUR WORKSPACE</small><h3>Business overview</h3></div><div className="mock-avatar">P</div></div>
                <div className="mock-stats"><div><span>New enquiries</span><b>Leads</b><em className="mock-green">Organized</em></div><div><span>Next actions</span><b>Follow-ups</b><em className="mock-purple">On track</em></div><div><span>Growth tools</span><b>Services</b><em className="mock-orange">Connected</em></div></div>
                <div className="mock-list-title"><b>Customer pipeline</b><span>Manage in one place</span></div>
                <div className="mock-row"><span className="mock-person">RK</span><div><b>Customer enquiry</b><small>Instant PAN Card</small></div><span className="mock-badge">New lead</span></div>
                <div className="mock-row"><span className="mock-person person-purple">AS</span><div><b>Service request</b><small>UDYAM Registration</small></div><span className="mock-badge badge-purple">Follow-up</span></div>
                <div className="mock-row"><span className="mock-person person-orange">MP</span><div><b>Business enquiry</b><small>Shop Act License</small></div><span className="mock-badge badge-orange">In progress</span></div>
              </div>
            </div>
            <div className="float-note"><span className="float-check">✓</span><div><b>Nothing slips through</b><small>Keep every enquiry in view</small></div></div>
          </div>
        </section>
        <section className="trust-strip"><span>BUILT FOR SERVICE BUSINESSES</span><div>Customer enquiries <i>✦</i> Follow-up workflows <i>✦</i> Multi-service teams <i>✦</i> Business growth</div></section>
        <section className="features-section section-pad" id="features">
          <div className="section-intro"><span className="eyebrow">A BETTER WAY TO WORK</span><h2>Less chasing.<br/><span>More growing.</span></h2><p>Keep your customer journey organized with tools designed to help you respond faster and work smarter.</p></div>
          <div className="feature-grid">
            <article className="feature-card"><div className="feature-icon fi-green">♧</div><h3>Lead management</h3><p>Bring enquiries from your campaigns and service pages into one organized workflow.</p><span className="feature-number">01</span></article>
            <article className="feature-card"><div className="feature-icon fi-purple">◷</div><h3>Follow-up tracking</h3><p>Keep track of conversations, next steps and the customers who need your attention.</p><span className="feature-number">02</span></article>
            <article className="feature-card"><div className="feature-icon fi-orange">◎</div><h3>Service-based enquiries</h3><p>Organize requests for PAN, UDYAM, Shop Act and other business services.</p><span className="feature-number">03</span></article>
            <article className="feature-card"><div className="feature-icon fi-blue">▤</div><h3>Business visibility</h3><p>Get a clearer view of your pipeline and make informed decisions as your team grows.</p><span className="feature-number">04</span></article>
          </div>
        </section>
        <section className="services-section section-pad" id="services">
          <div className="services-heading"><div><span className="eyebrow">ONE CRM, MANY SERVICES</span><h2>Made for the way<br/>your business works.</h2></div><p>Capture interest from individual service pages and bring customer enquiries into a central CRM workflow.</p></div>
          <div className="service-grid">
            <article className="service-card"><span className="service-symbol">ID</span><h3>Instant PAN Card</h3><p>Manage PAN-related customer enquiries and follow-ups.</p><span className="service-arrow">↗</span></article>
            <article className="service-card"><span className="service-symbol sym-purple">UD</span><h3>UDYAM Registration</h3><p>Keep business registration enquiries organized.</p><span className="service-arrow">↗</span></article>
            <article className="service-card"><span className="service-symbol sym-orange">SA</span><h3>Shop Act License</h3><p>Track requests and coordinate the next step.</p><span className="service-arrow">↗</span></article>
            <article className="service-card"><span className="service-symbol sym-blue">FL</span><h3>Food License</h3><p>Keep service enquiries visible to your team.</p><span className="service-arrow">↗</span></article>
          </div>
        </section>
        <section className="about-section section-pad" id="about"><div className="about-mark">P</div><div><span className="eyebrow">POWERED BY POTECH TECHNOLOGIES</span><h2>Your business deserves<br/>a clearer customer journey.</h2><p>Potech CRM is designed to help service businesses centralize customer enquiries and build a more consistent follow-up process — with room to grow as your business expands.</p><button className="primary-btn" onClick={() => navigate("login")}>Go to your workspace <span>→</span></button></div></section>
        <section className="contact-section section-pad" id="contact"><div><span className="eyebrow light-eyebrow">READY TO GET ORGANIZED?</span><h2>Let’s grow your business<br/>one lead at a time.</h2><p>Want to use Potech CRM for your organization? Get in touch with our team to discuss onboarding.</p></div><a className="contact-btn" href="mailto:info@potech.in?subject=Potech%20CRM%20Onboarding%20Enquiry">Talk to our team <span>→</span></a></section>
      </main>
      <footer className="site-footer"><button className="brand" onClick={() => navigate("home")}><span className="brand-mark">P</span><span>POTECH <b>CRM</b></span></button><span>© {new Date().getFullYear()} Potech Technologies Pvt Ltd. All rights reserved.</span><button className="footer-login" onClick={() => navigate("login")}>Client login ↗</button></footer>
    </div>
  );
}

export default App;
